import { Hidden, Modal } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import { Filter } from 'src/components/global/filter'
import { LoadingAnimation } from 'src/components/global/LoadingAnimation'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { SortbyDropdown } from 'src/components/global/SortbyDropdown'
import { useSearchSolutionsLazyQuery } from 'src/graphql/generated/hooks'
import { ISolutionListItemFragment } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { FilterOption } from 'src/types'

import { ConsolidationList } from '../ConsolidationList/ConsolidationList'
import { FilterOptions } from '../FilterOptions/FilterOptions'
import { Pagination } from '../Pagination/Pagination'

export type ConsolidationPaginationProp = {
  filterProps: FilterOption[]
  company?: string
  type: string
}

export const ConsolidationPagination: React.FC<ConsolidationPaginationProp> = ({ filterProps, company, type }) => {
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()

  const { session } = useAuthenticated()
  const token = session?.user.idToken || ''

  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [items, setItems] = useState<ISolutionListItemFragment[]>([])
  const [postsPerPage] = useState(10)
  const [showFilterDlg, setShowFilterDlg] = useState(false)
  const [searchGraveyardConsolidation, { data, loading }] = useSearchSolutionsLazyQuery()

  async function updatePage() {
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    const indexOfLastPost = curPage * postsPerPage
    const indexOfFirstPost = indexOfLastPost - postsPerPage
    const filters = router.query

    let selection: string[] = []
    if (filters.productType) {
      const paths = (filters.productType as string).split('_')
      const checks = paths[0].split(',')

      checks.map((check) => {
        const subTopicDocumentId = check.split('@@@@')[1]
        selection.push(subTopicDocumentId)
      })
    }
    selection = [...new Set(selection)]
    const newProductType = selection.join(',')
    const query = {
      offset: indexOfFirstPost,
      limit: postsPerPage,
      keyword: filters !== undefined ? (filters.keyword as string) : undefined,
      audiences: filters !== undefined ? (filters.audiences as string) : undefined,
      practiceAreas: filters !== undefined ? (filters.practiceAreas as string) : undefined,
      languages: filters !== undefined ? (filters.languages as string) : undefined,
      deployments: filters !== undefined ? (filters.deployments as string) : undefined,
      attributes: filters !== undefined ? (filters.attributes as string) : undefined,
      regionsServed: filters !== undefined ? (filters.regionsServed as string) : undefined,
      sort: filters?.sort || 'Default',
      topic: filters !== undefined ? (filters.topics as string) : undefined,
      subTopic: filters !== undefined ? (newProductType as string) : undefined,
      hqs: filters !== undefined ? (filters.hqs as string) : undefined,
      productType: type === 'graveyard' ? 'Graveyard' : 'Consolidation',
      integrations: filters !== undefined ? (filters.integrations as string) : undefined,
      company,
      includeCompany: 'true',
    }

    searchGraveyardConsolidation({ variables: { query } })
  }

  useEffect(() => {
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    if (data?.searchSolutions) {
      const items: ISolutionListItemFragment[] = (data?.searchSolutions?.solutions || []).filter(
        (vendor): vendor is ISolutionListItemFragment => !!vendor,
      )
      setItems(items)
      setTotalCount(data?.searchSolutions?.total || 0)
    }

    setCurrentPage(curPage)
  }, [data])

  useEffect(() => {
    updatePage()
  }, [currentPage, router, token, type])

  const navigateToPage = (page: number) => {
    updateQueryParams({ page: page.toString() }, true)
  }

  // Change page
  const paginateFront = () => {
    const lastpage = totalCount / postsPerPage
    if (currentPage < lastpage) {
      setCurrentPage(currentPage + 1)
      navigateToPage(currentPage + 1)
    }
  }
  const paginateBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      navigateToPage(currentPage - 1)
    }
  }
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber)
    navigateToPage(pageNumber)
  }

  return (
    <div className="flex flex-col flex-1 w-full pl-[10px] pr-[10px]">
      <Hidden mdDown>
        <FilterOptions filterOptions={filterProps} className="my-6" />

        <div className="flex flex-row justify-between items-center h-16 font-primary font-medium">
          {loading && (
            <div className="flex items-center">
              <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
            </div>
          )}
          {!loading && (
            <div className="font-primary font-medium">
              {(totalCount ?? 0) > 0
                ? `${totalCount ?? 0} Solution${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                : `No solution found`}
            </div>
          )}
          <SortbyDropdown
            className="w-54 font-primary font-medium"
            type="solutions"
            fromPage="graveyard-consolidations"
          />
        </div>
        {loading && <LoadingAnimation></LoadingAnimation>}
        {!loading && (
          <div>
            <ConsolidationList items={items} type={type} />
            {totalCount > 0 && (
              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={totalCount}
                paginateBack={paginateBack}
                paginateFront={paginateFront}
                paginate={paginate}
                currentPage={currentPage}
                className="pt-[1.875rem]"
              />
            )}
          </div>
        )}
      </Hidden>
      <Hidden mdUp>
        <FilterOptions filterOptions={filterProps} className="my-6" />

        <div className="flex flex-row justify-between md:items-center md:h-16 font-primary font-medium mb-3">
          <div className="flex flex-col">
            <Filter onClickFilter={() => setShowFilterDlg(true)} />
            {loading && (
              <div className="flex items-center">
                <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
              </div>
            )}
            {!loading && (
              <div className="font-primary font-medium mt-2 md:mt-0">
                {(totalCount ?? 0) > 0
                  ? `${totalCount ?? 0} Solution${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                  : `No solution found`}
              </div>
            )}
          </div>

          <SortbyDropdown
            className="w-54 font-primary font-medium md:mt-[-21px]"
            type="solutions"
            fromPage="graveyard-consolidations"
          />
        </div>
        <Modal open={showFilterDlg}>
          <div className="flex flex-col w-full h-full bg-white p-[17px]">
            <div className="flex flex-row mb-[13px]">
              <p className="font-secondary font-bold text-[20px] text-darkgray items-center">Filter</p>
              <div className="ml-auto" onClick={() => setShowFilterDlg(false)}>
                <IonIcon name="close-outline" className="!h-[30px] !w-[30px] text-[black]" />
              </div>
            </div>
            <SearchField className="mb-[23px]" />
            <Sidebar filterOptions={filterProps} defaultSelectFilterIdx={1} />
            <button
              className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy mt-auto mb-[15px] h-[50px] !important"
              onClick={() => setShowFilterDlg(false)}
            >
              Apply Filters
            </button>
          </div>
        </Modal>

        {loading && <LoadingAnimation></LoadingAnimation>}
        {!loading && (
          <div>
            <ConsolidationList items={items} type={type} />
            {totalCount > 0 && (
              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={totalCount}
                paginateBack={paginateBack}
                paginateFront={paginateFront}
                paginate={paginate}
                currentPage={currentPage}
                className="pt-[1.875rem]"
              />
            )}
          </div>
        )}
      </Hidden>
    </div>
  )
}
